import type { MatrixGroup, PermissionMatrix, RbacRole } from '../models/rbac.types';

/**
 * The reference's own roles and items, used when mocks are on.
 *
 * `RbacLogic` seeds every role from a level -- Admin is `all`, Supplier `read`
 * alone -- and these reproduce that, so the screen has something of the right
 * shape to draw without a backend.
 */

export const RBAC_MOCK_ROLES: RbacRole[] = [
  { id: 9, name: 'Admin', description: 'Full access across the organization, including configuration and hierarchy.', isDefault: true, grantedCount: 145 },
  { id: 7, name: 'Initiator', description: 'Raises purchase requests and sourcing documents for their department.', isDefault: true, grantedCount: 89 },
  { id: 6, name: 'Approver L1', description: 'Reviews and approves documents routed through workflow.', isDefault: true, grantedCount: 89 },
  { id: 8, name: 'Final Approver', description: 'Provides the final sign-off before a document is released.', isDefault: true, grantedCount: 117 },
  { id: 10, name: 'Supplier', description: 'External vendor who views invited RFQs and submits bids and documents.', isDefault: true, grantedCount: 61 },
];

const FUNCTIONS = [
  'Dashboard', 'Request For Quotation', 'Workflow Configuration',
  'Reverse Auction', 'Organization Setup', 'RBAC',
];

/*
  Feature names, as `os_feature` spells them -- the platform answers these, and `withDisplayLabels` turns
  them into the menu's labels ("RFQ Template" is drawn as "BID Template"). The masters are the nineteen the
  Masters menu lists, Designation included since V20260928120000 and Incoterm since V20261011110100.
*/
/** The items every role holds in full, drawn locked -- the platform's `RoleGrantService.ALWAYS_GRANTED_FEATURES`. */
export const ALWAYS_GRANTED = ['Dashboard'];

/**
 * Held in full and drawn locked for Admin and Mega Admin only -- the platform's
 * `RoleGrantService.ADMINISTRATOR_LOCKED_FEATURES`. Every other role's row is editable.
 */
export const ADMINISTRATOR_LOCKED = ['RBAC'];

/** Admin and Mega Admin, by the ids the platform is configured with (`admin-role-id`, `mega-admin-role-id`). */
export const ADMINISTERING_ROLE_IDS: readonly number[] = [9, 3];

/**
 * Managed by Mega Admin only: every other role draws these locked and kept as stored -- the platform's
 * `RoleGrantService.MEGA_ADMIN_MANAGED_FEATURES`.
 */
export const MEGA_ADMIN_MANAGED = ['Organization Setup'];
export const MEGA_ADMIN_ROLE_ID = 3;

const TEMPLATES = ['PR Template', 'RFQ Template', 'Supplier Onboarding'];

const MASTERS = [
  'Organization', 'Entity', 'Business Unit', 'Division', 'Department', 'Location',
  'Category', 'Unit of Measurement', 'Country', 'State', 'City', 'Product',
  'Currency', 'Payment Terms', 'Delivery Terms', 'Incoterm', 'Purchase Requests', 'Supplier', 'Designation',
  'User Management',
];

/**
 * The reference's per-level seed: [create, read, update, delete, view].
 *
 * ACCESS follows the platform's rule -- on wherever create, update or delete is -- and is also on for the
 * read-only Supplier, whose invited RFQs are still something it opens.
 */
function seedFor(roleId: number): [boolean, boolean, boolean, boolean, boolean] {
  switch (roleId) {
    case 9: return [true, true, true, true, true];
    case 7: return [true, true, false, false, true];
    case 8: return [false, true, true, true, true];
    case 10: return [false, true, false, false, true];
    case 6: return [false, true, true, false, true];
    /* Any other role -- one the screen just created -- holds READ alone, as the platform seeds it. */
    default: return [false, true, false, false, false];
  }
}

let nextId = 1;

function group(key: string, title: string, names: string[], roleId: number): MatrixGroup {
  const [create, read, update, del, view] = seedFor(roleId);
  const items = names.map((name) => {
    /*
      The Dashboard is fixed on for every role, as the platform holds it (V20260929100000); RBAC is fixed on
      for Admin and Mega Admin, so neither can lose the screen that manages roles; and every item is fixed on
      for Mega Admin -- the platform's `RoleGrantService.MATRIX_FEATURE_TYPES`.
    */
    const locked =
      ALWAYS_GRANTED.includes(name) ||
      (ADMINISTRATOR_LOCKED.includes(name) && ADMINISTERING_ROLE_IDS.includes(roleId)) ||
      roleId === MEGA_ADMIN_ROLE_ID;
    return {
      featureId: nextId++,
      name,
      create: locked || create,
      read: locked || read,
      update: locked || update,
      delete: locked || del,
      view: locked || view,
      ...(locked ? { locked: true } : {}),
      ...(MEGA_ADMIN_MANAGED.includes(name) && roleId !== MEGA_ADMIN_ROLE_ID ? { frozen: true } : {}),
    };
  });

  const granted = items.reduce(
    (sum, i) => sum + [i.view, i.create, i.read, i.update, i.delete].filter(Boolean).length,
    0,
  );

  return { key, title, itemCount: items.length, granted, total: items.length * 5, items };
}

export function RBAC_MOCK_MATRIX(roleId: number): PermissionMatrix {
  nextId = 1;
  const role = RBAC_MOCK_ROLES.find((r) => r.id === roleId) ?? RBAC_MOCK_ROLES[0];

  const groups = [
    group('FUNCTION', 'Functions', FUNCTIONS, roleId),
    group('TEMPLATE', 'Templates', TEMPLATES, roleId),
    group('MASTER', 'Masters', MASTERS, roleId),
  ];

  return {
    roleId,
    roleName: role?.name ?? 'Admin',
    roleDescription: role?.description ?? null,
    granted: groups.reduce((sum, g) => sum + g.granted, 0),
    total: groups.reduce((sum, g) => sum + g.total, 0),
    groups,
  };
}
